import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FolderOpen, Trash2, Download, Archive } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useI18n } from "@/i18n";
import { api, fileUrl, downloadBlob } from "@/lib/api";
import { PageHeader, StatusBadge, Empty } from "@/components/common";
import { Pick } from "@/app/tool/Options";
import ExportDialog from "@/app/ExportDialog";

export function ConfirmDelete({ text, onConfirm, testid }) {
  const { t } = useI18n();
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild><button className="p-1.5 text-neutral-500 hover:text-red-400" data-testid={testid} aria-label={t("common.delete")}><Trash2 size={15} /></button></AlertDialogTrigger>
      <AlertDialogContent className="bg-[var(--bg-1)] border-[var(--line)]">
        <AlertDialogHeader><AlertDialogTitle>{text}</AlertDialogTitle></AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testid}-cancel`}>{t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="bg-red-600 hover:bg-red-700" data-testid={`${testid}-confirm`}>{t("common.delete")}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ProjectCard({ p, selected, onSelect, onDelete, onExport }) {
  const { t, fmtDate } = useI18n();
  const r = p.results?.[0];
  const st = p.settings || {};
  return (
    <div className="panel overflow-hidden flex flex-col" data-testid={`project-card-${p.id}`}>
      <div className="grid grid-cols-2 gap-px bg-[var(--line)]">
        <img loading="lazy" src={fileUrl(p.source_file_id)} alt="" className="aspect-square object-contain bg-white w-full" />
        {r ? <img loading="lazy" src={fileUrl(r.thumb_file_id)} alt="" className="aspect-square object-contain bg-white w-full" /> : <div className="aspect-square bg-[var(--bg-2)]" />}
      </div>
      <div className="p-4 flex-1 flex flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          <p className="font-medium truncate" title={p.name}>{p.name}</p>
          <StatusBadge status={p.status} />
        </div>
        <dl className="text-xs text-neutral-400 grid grid-cols-2 gap-y-1">
          <dt>{t("projects.filter")}</dt><dd className="text-neutral-200">{t(`tools.${p.tool}.name`)}</dd>
          {["hero", "dimensions"].includes(p.tool) && <><dt>{t("tool.background")}</dt><dd className="flex items-center gap-1.5 font-mono"><span className="h-3 w-3 rounded-sm border border-neutral-600" style={{ background: r?.bg_hex || st.bg_hex }} />{r?.bg_hex || st.bg_hex}</dd></>}
          {p.tool !== "dimensions" && <><dt>{t("mk.label")}</dt><dd className="text-neutral-200" data-testid={`project-shot-type-${p.id}`}>{t(p.tool === "model" || st.with_model ? "mk.with" : "mk.without")}</dd></>}
          {st.model_id && <><dt>{t("tool.modelSource")}</dt><dd className="text-neutral-200 truncate">{t("tool.library")}</dd></>}
          <dt>{t("tool.size")}</dt><dd className="text-neutral-200">{r ? `${r.width}×${r.height}` : st.size}</dd>
          <dt>{t("billing.date")}</dt><dd className="text-neutral-200">{fmtDate(p.created_at)}</dd>
        </dl>
        <div className="mt-auto pt-2 flex items-center gap-2">
          {r && <Checkbox checked={selected} onCheckedChange={onSelect} aria-label={t("tool.selectForZip")} data-testid={`project-select-${p.id}`} />}
          <Link to={`/app/${p.tool}?project=${p.id}`} className="text-sm text-emerald-400 hover:underline mr-auto" data-testid={`project-open-${p.id}`}>{t("tool.openProject")}</Link>
          {r && <button onClick={() => onExport(r.id)} className="p-1.5 text-neutral-400 hover:text-white" data-testid={`project-download-${p.id}`} aria-label={t("common.download")}><Download size={15} /></button>}
          <ConfirmDelete text={t("projects.deleteConfirm")} onConfirm={onDelete} testid={`project-delete-${p.id}`} />
        </div>
      </div>
    </div>
  );
}

export default function Projects() {
  const { t, errMsg } = useI18n();
  const [tool, setTool] = useState("all");
  const [shot, setShot] = useState("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  const [exportId, setExportId] = useState(null);
  const load = useCallback(() => {
    const params = { page, limit: 12, ...(tool !== "all" ? { tool } : {}), ...(shot !== "all" ? { with_model: shot === "with" } : {}) };
    api.get("/projects", { params }).then((r) => setData(r.data));
  }, [page, tool, shot]);
  useEffect(load, [load]);
  const del = async (id) => { try { await api.delete(`/projects/${id}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const ids = Object.values(sel);
  const zip = async () => { try { await downloadBlob("/export/zip", null, "post", { result_ids: ids, fmt: "webp", preset: "web" }); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div data-testid="projects-page">
      <PageHeader title={t("projects.title")} sub={data && t("projects.results", { n: data.total })}>
        <div className="flex items-center gap-3">
          {ids.length > 0 && <button onClick={zip} className="btn-brand rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-2" data-testid="projects-zip-btn"><Archive size={15} />{t("tool.zip", { n: ids.length })}</button>}
          <div className="w-44"><Pick value={tool} onChange={(v) => { setTool(v); setPage(1); }} options={[["all", t("common.all")], ...["hero", "model", "lifestyle", "dimensions"].map((k) => [k, t(`tools.${k}.name`)])]} testid="projects-tool-filter" /></div>
          <div className="w-40"><Pick value={shot} onChange={(v) => { setShot(v); setPage(1); }} options={[["all", t("common.all")], ["with", t("mk.with")], ["without", t("mk.without")]]} testid="projects-shot-filter" /></div>
        </div>
      </PageHeader>
      {data && data.items.length === 0 && <Empty icon={FolderOpen} text={t("projects.empty")} />}
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
        {data?.items.map((p) => (
          <ProjectCard key={p.id} p={p} selected={!!sel[p.id]} onExport={setExportId} onDelete={() => del(p.id)}
            onSelect={(c) => setSel((x) => { const n = { ...x }; if (c) n[p.id] = p.results[0].id; else delete n[p.id]; return n; })} />
        ))}
      </div>
      {data && data.pages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-8 text-sm">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="disabled:opacity-40" data-testid="projects-prev-btn">{t("common.prev")}</button>
          <span>{page} {t("common.of")} {data.pages}</span>
          <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="disabled:opacity-40" data-testid="projects-next-btn">{t("common.next")}</button>
        </div>
      )}
      <ExportDialog resultId={exportId} open={!!exportId} onOpenChange={(o) => !o && setExportId(null)} />
    </div>
  );
}
